import countries from "./countries";
import provinces from "./provinces";
import states from "./states";

/**
 * Map an arbitrary stored country value to the 2-letter code the form's
 * country dropdown is keyed by. Accepts a value that is already a code
 * (e.g. "CA") or a full country name (e.g. "Canada"). Returns "" when the
 * value matches no known country, so the dropdown renders empty and the
 * admin is prompted to pick one.
 */
export function normalizeCountryToCode(country: string | undefined): string {
  if (!country) {
    return "";
  }

  const match = countries.find(
    (c) =>
      c.value.toUpperCase() === country.toUpperCase() ||
      c.label.toUpperCase() === country.toUpperCase(),
  );

  return match?.value ?? "";
}

const regionOptionsByCountryCode: Record<
  string,
  { label: string; value: string }[]
> = {
  CA: provinces,
  US: states,
};

/**
 * Map an arbitrary stored region value to the code the form's Region dropdown
 * is keyed by, using the (already normalized) country code to pick the right
 * option list. Accepts a value that is already a code (e.g. "MB"), a full name
 * (e.g. "Manitoba"), or an ISO 3166-2 value (e.g. "CA-MB"). Returns "" when it
 * matches no option, so the dropdown renders empty and prompts a re-pick.
 */
export function normalizeRegionToCode(
  countryCode: string,
  region: string | undefined,
): string {
  if (!region) {
    return "";
  }

  const options = regionOptionsByCountryCode[countryCode] ?? [];
  const bareRegion = region.includes("-")
    ? region.slice(region.lastIndexOf("-") + 1)
    : region;

  const match = options.find(
    (option) =>
      option.value.toUpperCase() === bareRegion.toUpperCase() ||
      option.label.toUpperCase() === bareRegion.toUpperCase(),
  );

  return match?.value ?? "";
}
